import { CoreState, DeploymentState } from 'common/CommonConstants';
import type { ComponentWithValues } from 'common/stack/ComponentWithValuesDocuments';
import { StatusItemState } from 'common/status/StatusDocument';
import type { StatusTree, StatusTreePendingChanges, StatusTreePodItem, StatusTreeStorageItem } from 'common/status/StatusTree';

export interface ComponentsStatusTree {
  version: number;
  itemStateID: StatusItemState;
  hasBeenGreen?: true; // Client only value
  coreHasBeenGreen?: true; // Client only value
  timeLatestStart?: string; // Client only value
  isStarting?: true; // Client only value
  deploymentStateID: DeploymentState; // Client only value, always DeploymentState.RUNNING in core

  pendingChanges: StatusTreePendingChanges;

  core: {
    itemStateID: StatusItemState;
    coreStateID: CoreState;
    timeRunUntil: string | null;
  };

  components: ComponentStatusTree[];
}

export interface ComponentStatusTree {
  stackComponentID: number;
  itemStateID: StatusItemState;
  storage: StatusTreeStorageItem[];

  // TODO: This used to have a top level "message" field for errors, what to do with that?
  containers: StatusTreePodItem[];
}

interface ConvertStatusTreeToComponentsStatusParams {
  statusTree: StatusTree | null | undefined;

  // TODO: This is here just to make the components appear before they actually appear in the status tree
  componentsWithValues: ComponentWithValues[];
}

export const convertStatusTreeToComponentsStatusTree = ({
  statusTree,
  componentsWithValues,
}: ConvertStatusTreeToComponentsStatusParams): ComponentsStatusTree => {
  const stackComponentIDs = [
    ...new Set(
      [
        ...(statusTree?.storage.items || []).map((item) => item.stackComponentID),
        ...(statusTree?.containers.items || []).map((item) => item.stackComponentID),
        ...componentsWithValues.map((component) => component.stackComponentID),
      ].filter((id) => id !== null)
    ),
  ];

  const components: ComponentStatusTree[] = stackComponentIDs.map((stackComponentID) => {
    const storage = statusTree?.storage.items.filter((item) => item.stackComponentID === stackComponentID) || [];
    const containers = statusTree?.containers.items.filter((item) => item.stackComponentID === stackComponentID) || [];

    let itemStateID = StatusItemState.GRAY;
    if ([...storage, ...containers].some((item) => item.itemStateID === StatusItemState.GREEN)) {
      itemStateID = StatusItemState.GREEN;
    }
    if ([...storage, ...containers].some((item) => item.itemStateID === StatusItemState.YELLOW)) {
      itemStateID = StatusItemState.YELLOW;
    }
    if ([...storage, ...containers].some((item) => item.itemStateID === StatusItemState.RED)) {
      itemStateID = StatusItemState.RED;
    }

    return {
      stackComponentID,
      itemStateID,
      storage,
      containers,
    };
  });

  return {
    version: 1,
    itemStateID: statusTree?.itemStateID || StatusItemState.GRAY,
    hasBeenGreen: statusTree?.hasBeenGreen,
    coreHasBeenGreen: statusTree?.coreHasBeenGreen,
    timeLatestStart: statusTree?.timeLatestStart,
    isStarting: statusTree?.isStarting,
    deploymentStateID: statusTree?.deploymentStateID || DeploymentState.STOPPED,

    pendingChanges: {
      state: statusTree?.pendingChanges.state || 'no-change',
      errorCode: statusTree?.pendingChanges.errorCode || null,
      errorDetails: statusTree?.pendingChanges.errorDetails || null,
      timeUpdated: statusTree?.pendingChanges.timeUpdated || null,
      components: statusTree?.pendingChanges.components || [],
    },

    core: {
      itemStateID: statusTree?.core.itemStateID || StatusItemState.GRAY,
      coreStateID: statusTree?.core.coreStateID || CoreState.DELETED,
      timeRunUntil: statusTree?.core.timeRunUntil || null,
    },

    components: components,
  };
};
